/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Col, Row, Select } from "antd";
import { GlobalContext } from "contexts/GlobalContext";
import {
  GRAPH_TABLE_FILTER_LABELS,
  Sticky_Delta_Strike_LABELS,
} from "constants/generalActionButtons";
import { useLocation } from "react-router-dom";
import { VolSurfaceContext } from "contexts/VolSurfaceContext";
import "./FilterButtons.scss";
import { MarketAnalysisContext } from "contexts/MarketAnalysisContext";
import useDebounce from "hooks/useDebounce";
import { SHORTCUT_FILTER } from "constants/volSurface";
import {
  getDataFromLocalStorage,
  isDataMember,
  isTrader,
} from "helpers/generalHelper";
import RiskFree from "./RiskFree";
import SaveVolSurface from "./SaveVolSurface";
import SetLiveSurface from "./SetLiveSurface";
import SetComparisonSurface from "./SetComparisonSurface";
import WholeSurface from "./WholeSurface";
import Shortcuts from "./Shortcuts";
import SendEmail from "./SendEmail";
import DateAndExport from "./DateAndExport";
import DownloadButton from "./DownloadButton";
import GraphOrTableTabs from "./GraphOrTableTabs";
import StickyDeltaOrStrickTabs from "./StickyDeltaOrStrickTabs";
import SelectDateRange from "./SelectDateRange";
import SelectDate from "./SelectDate";
import StickyDeltaSurface from "./StickyDeltaSurface";
import AverageAndCostOfCarry from "./AverageAndCostOfCarry";
import { checkForApoShortcutName } from "helpers/volSurfaceHelper";
import BrokingCompanies from "./BrokingCompanies";
import SendSFTPFile from "./SendSFTPFile";
import YourMarksCalcDate from "./YourMarksCalcDate";
import { useVolSurfaceControlTable } from "contexts/VolSurfaceControlTableContext";
import TraderSurfaces from "./TraderSurfaces";

const FilterButton = ({
  selectedTab,
  setSelectedTab,
  start_date,
  end_date,
  initial_date,
  selectedFilters,
  setSelectedFilters,
  onBiasChange,
  batchUpdate,
  onUpdateGraphTitle,
  contractFilterLoading,
  setComparisonGraph,
  periodTypeFilterLoading,
  comparisonGraph,
  isRiskFreeRevert,
  setIsRiskFreeRevert,
  initialRiskFree,
  surface_id,
  setCurrentTab,
  currentTab,
  setEnd_date,
  setStart_date,
  dateTime,
  setDateTime,
  comparison_surface_id,
  setComparisonSurfaceId,
  setIsCalculationDateChanged,
  initialAverage,
  initialCostOfCarry,
  initialMarksCalcDate,
}) => {
  const location = useLocation();
  const {
    globalState: { dark_Theme, loginInfo },
  } = useContext(GlobalContext);
  const { lockedData } = useVolSurfaceControlTable();
  const { state } = useContext(VolSurfaceContext);
  const { MarketAnalysisState } = useContext(MarketAnalysisContext);
  const [riskFree, setRiskFree] = useState(initialRiskFree);
  const [average, setAverage] = useState(initialAverage);
  const [marksCalcDate, setMarksCalcDate] = useState(initialMarksCalcDate);
  const [costOfCarry, setCostOfCarry] = useState(initialCostOfCarry);
  const [shortcutFilter, setShortcutFilter] = useState(
    getDataFromLocalStorage(SHORTCUT_FILTER)
  );
  const debouncedRiskFree = useDebounce(riskFree, 1000);
  const debouncedAverage = useDebounce(average, 1000);
  const debouncedCostOfCarry = useDebounce(costOfCarry, 1000);
  const notTrader = !isTrader();

  useEffect(() => {
    setRiskFree(initialRiskFree);
  }, [initialRiskFree, lockedData]);

  useEffect(() => {
    setAverage(initialAverage);
  }, [initialAverage, lockedData]);

  useEffect(() => {
    setMarksCalcDate(initialMarksCalcDate);
  }, [initialMarksCalcDate, lockedData]);

  useEffect(() => {
    setCostOfCarry(initialCostOfCarry);
  }, [initialCostOfCarry, lockedData]);

  const disabledGraphControl = useMemo(() => {
    if (currentTab === Sticky_Delta_Strike_LABELS.STICKY_STRIKE.key)
      return false;
    let filters = { ...selectedFilters };
    delete filters.risk_free;
    if (filters && Object.keys(filters).length >= 4) {
      return false;
    }
    return true;
  }, [selectedFilters, currentTab]);

  useEffect(() => {
    setSelectedFilters((prev) => ({ ...prev, risk_free: debouncedRiskFree }));
  }, [debouncedRiskFree]);

  useEffect(() => {
    setSelectedFilters((prev) => ({
      ...prev,
      calculation_date: marksCalcDate,
    }));
  }, [marksCalcDate]);

  useEffect(() => {
    setSelectedFilters((prev) => ({ ...prev, average: debouncedAverage }));
  }, [debouncedAverage, average]);

  useEffect(() => {
    setSelectedFilters((prev) => ({
      ...prev,
      cost_of_carry: debouncedCostOfCarry,
    }));
  }, [debouncedCostOfCarry, costOfCarry]);

  useEffect(() => {
    if (selectedFilters?.shortcutFilter) {
      setShortcutFilter(selectedFilters.shortcutFilter);
    }
  }, [selectedFilters]);

  useEffect(() => {
    if (shortcutFilter) {
      let selectedShortcutFilter = state.volSurfaceFilterShortcuts
        ? state.volSurfaceFilterShortcuts.filter(
            (short) => short.value === shortcutFilter
          )
        : null;
      if (selectedShortcutFilter && selectedShortcutFilter[0]) {
        selectedShortcutFilter = { ...selectedShortcutFilter[0] };
        delete selectedShortcutFilter.label;
        delete selectedShortcutFilter.value;
        let newFilters = {
          ...selectedFilters,
          shortcutFilter: shortcutFilter,
          ...selectedShortcutFilter,
          product_id: `${selectedShortcutFilter.product_id}`,
        };
        setSelectedFilters(newFilters);
      }
    } else if (shortcutFilter === null) {
      let newFilters = { ...selectedFilters, shortcutFilter: null };
      setSelectedFilters(newFilters);
    }
  }, [shortcutFilter]);

  const showFilterButtonsForGraphs = useMemo(() => {
    const isTableOrMarket =
      location.pathname.includes("market-analysis") ||
      (selectedTab === GRAPH_TABLE_FILTER_LABELS.TABLE &&
        currentTab === Sticky_Delta_Strike_LABELS.STICKY_DELTA.key);

    const disabledCondition =
      !selectedFilters["product_id"] || state?.volSurfaceBtnLoading;

    return (
      <Row gutter={[25, 25]} className="bias-btn">
        {!isTableOrMarket && (
          <>
            <RiskFree
              notTrader={notTrader}
              dark_Theme={dark_Theme}
              setRiskFree={setRiskFree}
              riskFree={riskFree}
              disabledCondition={disabledCondition}
              selectedFilters={selectedFilters}
            />
            {currentTab === Sticky_Delta_Strike_LABELS.STICKY_DELTA.key &&
              !isTableOrMarket &&
              checkForApoShortcutName(selectedFilters) && (
                <AverageAndCostOfCarry
                  average={average}
                  notTrader={notTrader}
                  costOfCarry={costOfCarry}
                  dark_Theme={dark_Theme}
                  setAverage={setAverage}
                  setCostOfCarry={setCostOfCarry}
                  disabledCondition={disabledCondition}
                  selectedFilters={selectedFilters}
                />
              )}
            {notTrader && (
              <SaveVolSurface
                dark_Theme={dark_Theme}
                selectedFilters={selectedFilters}
                disabledGraphControl={disabledGraphControl}
                onUpdateGraphTitle={onUpdateGraphTitle}
              />
            )}
          </>
        )}

        {currentTab === Sticky_Delta_Strike_LABELS.STICKY_DELTA.key &&
          !isTableOrMarket && (
            <>
              {notTrader &&
              loginInfo?.authorized_permissions?.includes(
                "show-live-surface"
              ) ? (
                <SetLiveSurface
                  dark_Theme={dark_Theme}
                  setComparisonGraph={setComparisonGraph}
                  userVolSurfaces={state?.userVolSurfaces}
                  setSelectedFilters={setSelectedFilters}
                  disabledCondition={disabledCondition}
                  selectedFilters={selectedFilters}
                />
              ) : null}

              {notTrader && (
                <SetComparisonSurface
                  dark_Theme={dark_Theme}
                  comparisonGraph={comparisonGraph}
                  setComparisonGraph={setComparisonGraph}
                  setSelectedFilters={setSelectedFilters}
                  userVolSurfaces={state?.userVolSurfaces}
                  disabledCondition={disabledCondition}
                  comparison_surface_id={comparison_surface_id}
                  setComparisonSurfaceId={setComparisonSurfaceId}
                />
              )}
              {notTrader && (
                <>
                  <StickyDeltaSurface
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                  />
                  {/* Bias Controller */}
                  <WholeSurface
                    dark_Theme={dark_Theme}
                    batchUpdate={batchUpdate}
                    onBiasChange={onBiasChange}
                    disabledCondition={disabledCondition}
                  />
                </>
              )}
            </>
          )}
      </Row>
    );
  }, [
    currentTab,
    location.pathname,
    selectedTab,
    selectedFilters,
    disabledGraphControl,
    state?.volSurfaceBtnLoading,
    state?.userVolSurfaces,
    riskFree,
    dark_Theme,
    comparisonGraph,
    batchUpdate,
    onUpdateGraphTitle,
    setSelectedFilters,
    setComparisonGraph,
    onBiasChange,
  ]);

  const filterButton = (filterState, parentKey, allowClear) => {
    const handleChange = (values) => {
      setSelectedFilters((prev) => ({
        ...prev,
        shortcutFilter: null,
        comparison_surface_id: null,
        live_surface_id: null,
      }));
      if (setComparisonGraph) {
        setComparisonGraph(undefined);
      }

      if (!values || values.length === 0) {
        const { [parentKey]: _, ...rest } = selectedFilters;
        setSelectedFilters({
          ...rest,
          shortcutFilter: null,
          product_id: rest.product_id ? rest.product_id : null,
        });
      } else {
        setSelectedFilters((prev) => ({
          ...prev,
          [parentKey]: values,
        }));
      }
    };

    return (
      <Select
        placeholder="Select filters"
        value={selectedFilters?.[parentKey] || []}
        onChange={(values) => {
          setShortcutFilter(null);
          handleChange(values);
        }}
        showSearch
        allowClear={allowClear}
        filterOption={(input, option) => {
          const inputSmallLetter = input.toLowerCase();
          const label = option.children.toLowerCase();
          return label.includes(inputSmallLetter);
        }}
        style={{ width: 180 }}
        disabled={state?.volSurfaceBtnLoading}
        className={dark_Theme ? "select-dark" : ""}
        popupClassName={dark_Theme ? "dropdown-dark" : ""}
        loading={
          parentKey === "contract_style"
            ? contractFilterLoading
            : parentKey === "period_id"
            ? periodTypeFilterLoading
            : false
        }
      >
        {filterState?.map((item) => (
          <Select.Option key={JSON.stringify(item)} value={item.value}>
            {item.label}
          </Select.Option>
        ))}
      </Select>
    );
  };

  const showVolSurfaceFilters = useMemo(() => {
    if (
      location.pathname.includes("market-analysis") ||
      location.pathname.includes("vol-surface")
    )
      return null;
    return state?.volSurfaceFilters?.map((item, index) => {
      return (
        <div style={{ marginBottom: 10 }} key={JSON.stringify(item)}>
          <Row>
            <Col span={3}>
              <div>{item.label}</div>
            </Col>
            <Col>{filterButton(item.data, item.key, true)}</Col>
          </Row>
        </div>
      );
    });
  }, [
    state?.volSurfaceFilters,
    selectedFilters,
    contractFilterLoading,
    periodTypeFilterLoading,
    state?.volSurfaceBtnLoading,
    selectedTab,
  ]);

  const showMarketAnalysisFilters = useMemo(() => {
    if (selectedTab === "Table") return null;
    return MarketAnalysisState?.marketAnalysisFilters?.map((item, index) => {
      return (
        <div style={{ marginBottom: 10 }} key={JSON.stringify(item)}>
          <Row>
            <Col span={3}>
              <div>{item.label}</div>
            </Col>
            <Col>{filterButton(item.data, item.key, false)}</Col>
          </Row>
        </div>
      );
    });
  }, [
    MarketAnalysisState?.marketAnalysisFilters,
    selectedFilters,
    selectedTab,
  ]);

  const handleShortcutChange = (shortcutId) => {
    if (shortcutId) {
      setShortcutFilter(shortcutId);
      if (setComparisonGraph) {
        setComparisonGraph(undefined);
      }
    } else {
      setShortcutFilter(null);
      setSelectedFilters((prev) => ({
        ...prev,
        shortcutFilter: null,
      }));
    }
  };

  const volSurfaceDataPath =
    isTrader() ||
    isDataMember() ||
    location.pathname.includes("vol-surface-data");
  const marketAnalysisPath = location.pathname.includes("market-analysis");
  const disabledCondition =
    !selectedFilters["product_id"] || state?.volSurfaceBtnLoading;
  return (
    <Row gutter={[0, 12]}>
      {!volSurfaceDataPath && (
        <>
          {notTrader && (volSurfaceDataPath || !marketAnalysisPath) && (
            <StickyDeltaOrStrickTabs
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
            />
          )}
          {currentTab === Sticky_Delta_Strike_LABELS.STICKY_STRIKE.key && (
            <SelectDate
              dateTime={dateTime}
              setDateTime={setDateTime}
              setSelectedFilters={setSelectedFilters}
              setIsCalculationDateChanged={setIsCalculationDateChanged}
            />
          )}
          {notTrader &&
            currentTab !== Sticky_Delta_Strike_LABELS.STICKY_STRIKE.key && (
              <>
                <GraphOrTableTabs
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                />
                {selectedTab === GRAPH_TABLE_FILTER_LABELS.GRAPHS &&
                  !volSurfaceDataPath && (
                    <YourMarksCalcDate
                      dateTime={marksCalcDate}
                      setDateTime={setMarksCalcDate}
                      setSelectedFilters={setSelectedFilters}
                      disabledCondition={disabledCondition}
                    />
                  )}
                <SelectDateRange
                  dark_Theme={dark_Theme}
                  end_date={end_date}
                  start_date={start_date}
                  setEnd_date={setEnd_date}
                  setStart_date={setStart_date}
                />
              </>
            )}
        </>
      )}

      {!notTrader && (
        <BrokingCompanies
          dark_Theme={dark_Theme}
          setSelectedFilters={setSelectedFilters}
          selectedFilters={selectedFilters}
        />
      )}

      {(volSurfaceDataPath || !marketAnalysisPath) && (
        <Shortcuts
          dark_Theme={dark_Theme}
          handleShortcutChange={handleShortcutChange}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          volSurfaceFilterShortcuts={state?.volSurfaceFilterShortcuts}
          currentTab={currentTab}
          setComparisonSurfaceId={setComparisonSurfaceId}
        />
      )}

      {!notTrader && <TraderSurfaces selectedFilters={selectedFilters} />}

      {!(marketAnalysisPath || volSurfaceDataPath) && (
        <>
          {currentTab === Sticky_Delta_Strike_LABELS.STICKY_DELTA.key && (
            <>
              {notTrader && <SendEmail dark_Theme={dark_Theme} />}
              {loginInfo?.show_company_sftp && <SendSFTPFile />}
            </>
          )}

          <Col span={24}>{showVolSurfaceFilters}</Col>
          <Col style={{ marginTop: 0 }} span={24}>
            {showFilterButtonsForGraphs}
          </Col>
        </>
      )}

      {marketAnalysisPath && (
        <>
          <Col span={24}>{showMarketAnalysisFilters}</Col>
          {selectedTab === "Table" && (
            <DateAndExport
              dark_Theme={dark_Theme}
              end_date={end_date}
              start_date={start_date}
            />
          )}
        </>
      )}
      {selectedFilters?.product_id && volSurfaceDataPath && notTrader && (
        <DownloadButton
          dark_Theme={dark_Theme}
          selectedFilters={selectedFilters}
          surface_id={surface_id}
        />
      )}
    </Row>
  );
};

export default FilterButton;
